<!-- ============================================
     푸터: RouterLink + vue-i18n (ko / en / ja)
     언어 변경: mainStore + @/i18n.js changeLanguage (짧은 코드)
     ============================================ -->

<template>
  <footer class="bg-light text-dark pt-5 pb-4 overflow-hidden">
    <div class="container">
      <section class="row gy-4 justify-content-between">
        <div class="col-lg-7 col-md-12 col-12">
          <div class="d-flex flex-wrap align-items-center justify-content-center justify-content-lg-start mb-3 gap-2"
            role="group" :aria-label="t('footer.aria.brandGroup')">
            <RouterLink to="/" class="footer-logo d-inline-flex align-items-center">
              <img src="@/assets/resources/img/mpnite-footer-logo.png" alt="MPNITE" height="24" width="auto" />
              <span class="visually-hidden">{{ t("footer.aria.homeLink") }}</span>
            </RouterLink>

            <img src="@/assets/resources/img/by-footer-logo.png" alt="" height="24" width="auto" role="presentation"
              aria-hidden="true" />

            <a href="https://urbas.kr" class="footer-logo d-inline-flex align-items-center" target="_blank"
              rel="noopener noreferrer">
              <img src="@/assets/resources/img/urbas-footer-logo.png" alt="URBAS" height="24" width="auto" />
              <span class="visually-hidden">{{
                t("footer.aria.urbasExternal")
              }}</span>
            </a>
          </div>

          <div class="small text-secondary text-center text-lg-start">
            <p class="mb-1">
              {{ t("footer.company.line1") }}
              <span class="d-none d-sm-inline">|</span>
              <br class="d-sm-none" />
              {{ t("footer.company.line1Ceo") }}
            </p>
            <p class="mb-1">
              {{ t("footer.company.csLabel") }} :
              <a href="tel:+82-70-4348-0089" class="footer-link">070-4348-0089</a>
              <span class="d-none d-sm-inline"> | </span>
              <br class="d-sm-none" />
              {{ t("footer.company.emailLabel") }} :
              <a href="mailto:mpnite@urbas.kr" class="footer-link">mpnite@urbas.kr</a>
            </p>
            <address class="d-block mb-1">
              {{ t("footer.company.addressLabel") }}:
              {{ t("footer.company.address") }}
            </address>

            <div class="footer-cert mt-3 text-center text-lg-start">
              <div
                class="d-flex flex-wrap align-items-center justify-content-center justify-content-lg-start gap-2 mb-2"
                role="group" aria-label="품질경영시스템 인증">
                <img src="@/assets/resources/img/iso9001.png" alt="ISO 9001 인증" class="footer-cert-logo" height="48"
                  width="auto" loading="lazy" decoding="async" />
                <img src="@/assets/resources/img/kab.jpg" alt="KAB 한국인정지원센터" class="footer-cert-logo" height="48"
                  width="auto" loading="lazy" decoding="async" />
                <img src="@/assets/resources/img/iaf.png" alt="IAF" class="footer-cert-logo" height="48" width="auto"
                  loading="lazy" decoding="async" />
                <div>
                  <p class="small text-secondary mb-0">
                    ISO 9001:2015 품질경영시스템 인증 획득
                  </p>
                  <p class="small text-secondary mb-0">
                    (인증기관: 한국표준경영원)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-4 col-md-12 col-12">
          <div class="row text-center text-lg-start">
            <div class="col-6 mb-3 mb-lg-0">
              <div class="footer-heading fw-bold text-secondary mb-3">
                {{ t("footer.section.more") }}
              </div>
              <nav :aria-label="t('footer.aria.moreNav')">
                <ul class="list-unstyled small d-flex flex-column gap-2">
                  <li v-for="item in MORE_MENU" :key="item.path">
                    <RouterLink :to="item.path" class="footer-link">
                      {{ item.label }}
                    </RouterLink>
                  </li>
                </ul>
              </nav>
            </div>

            <div class="col-6">
              <div class="footer-heading fw-bold text-secondary mb-3">
                {{ t("footer.section.whoWeAre") }}
              </div>
              <nav :aria-label="t('footer.aria.socialNav')">
                <ul class="list-unstyled small d-flex flex-column gap-2">
                  <li v-for="item in SOCIAL_LINKS" :key="item.url || item.label">
                    <a v-if="item.url" :href="item.url" class="footer-link d-inline-flex align-items-center gap-2"
                      target="_blank" rel="noopener noreferrer">
                      <i :class="item.icon" aria-hidden="true"></i>
                      <span>{{ item.label }}</span>
                      <span class="visually-hidden">{{
                        t("footer.aria.opensNewTab")
                      }}</span>
                    </a>
                    <button v-else type="button"
                      class="footer-link footer-btn-link d-inline-flex align-items-center gap-2"
                      @click="handleComingSoon">
                      <i :class="item.icon" aria-hidden="true"></i>
                      <span>{{ item.label }}</span>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </section>

      <hr class="my-4" />

      <section class="d-flex flex-column flex-lg-row justify-content-between align-items-center text-center gap-3">
        <nav :aria-label="t('footer.aria.policyNav')">
          <ul class="list-inline mb-0 small">
            <template v-for="(item, index) in POLICY_LINKS" :key="item.path">
              <li class="list-inline-item my-1">
                <RouterLink :to="item.path" class="footer-link fw-semibold">
                  {{ item.label }}
                </RouterLink>
              </li>
              <li v-if="index < POLICY_LINKS.length - 1" class="list-inline-item text-muted" aria-hidden="true">
                |
              </li>
            </template>
          </ul>
        </nav>

        <div class="small" role="group" :aria-label="t('footer.aria.langSelect')">
          <button type="button" class="footer-link footer-btn-link"
            :class="{ 'fw-bold text-dark': mainStore.language === 'en' }" :aria-pressed="mainStore.language === 'en'"
            @click="changeFooterLanguage('en')">
            {{ t("footer.lang.en") }}
          </button>
          <span class="mx-2 text-muted" aria-hidden="true">|</span>
          <button type="button" class="footer-link footer-btn-link"
            :class="{ 'fw-bold text-dark': mainStore.language === 'ko' }" :aria-pressed="mainStore.language === 'ko'"
            @click="changeFooterLanguage('ko')">
            {{ t("footer.lang.ko") }}
          </button>
          <span class="mx-2 text-muted" aria-hidden="true">|</span>
          <button type="button" class="footer-link footer-btn-link"
            :class="{ 'fw-bold text-dark': mainStore.language === 'ja' }" :aria-pressed="mainStore.language === 'ja'"
            @click="changeFooterLanguage('ja')">
            {{ t("footer.lang.ja") }}
          </button>
        </div>
      </section>

      <p class="text-center text-secondary small mt-4 mb-0 opacity-75">
        {{ t("footer.copyright", { year: currentYear }) }}
      </p>
    </div>
  </footer>
</template>

<script setup>
import { computed } from "vue";
import { RouterLink } from "vue-router";
import { useI18n } from "vue-i18n";
import { useMainStore } from "@/store/mainStore";
import { changeLanguage as applyI18nLanguage } from "@/i18n.js";

defineOptions({ name: "FooterComponents" });

const mainStore = useMainStore();
const { t, locale } = useI18n({ useScope: "global" });

const currentYear = new Date().getFullYear();

const LOCALE_BY_LANG = Object.freeze({
  ko: "ko-KR",
  en: "en-US",
  ja: "ja-JP",
});

const MORE_MENU = computed(() => {
  void locale.value;
  return [
    { label: t("footer.more.service"), path: "/serviceinfo" },
    { label: t("footer.more.materials"), path: "/materialinfo" },
    { label: t("footer.more.guide"), path: "/board" },
    { label: t("footer.more.partnerInquiry"), path: "/partner/inquiry" },
  ];
});

const SOCIAL_LINKS = computed(() => {
  void locale.value;
  return [
    { label: t("footer.social.urbas"), url: "https://urbas.kr", icon: "bi bi-building" },
    {
      label: t("footer.social.youtubeShorts"),
      url: "https://www.youtube.com/@MPNITE_COM/shorts",
      icon: "bi bi-youtube",
    },
    {
      label: t("footer.social.naverBlog"),
      url: "https://blog.naver.com/mpnite_info",
      icon: "bi bi-postcard",
    },
    {
      label: t("footer.social.globalBlog"),
      url: "https://mpnite.com/blog",
      icon: "bi bi-journal-text",
    },
  ];
});

const POLICY_LINKS = computed(() => {
  void locale.value;
  return [
    { label: t("footer.policy.about"), path: "/about" },
    { label: t("footer.policy.terms"), path: "/useterm" },
    { label: t("footer.policy.privacy"), path: "/privacyterm" },
    { label: t("footer.policy.security"), path: "/security" },
    { label: t("footer.policy.platformAgreement"), path: "/platformagreement" },
    { label: t("footer.policy.supportPolicy"), path: "/support-policy" },
  ];
});

function handleComingSoon() {
  alert(t("footer.alert.comingSoon"));
}

function changeFooterLanguage(lang) {
  const code = lang === "en" || lang === "ko" || lang === "ja" ? lang : "ko";
  mainStore.SET_LANGUAGE(code);
  mainStore.SET_LOCALE(LOCALE_BY_LANG[code] || "ko-KR");
  applyI18nLanguage(code);
}
</script>

<style scoped>
.container {
  max-width: 1280px;
}

.footer-link {
  color: #6c757d;
  text-decoration: none;
  transition: 0.2s;
}

.footer-link:hover {
  color: #212529;
  text-decoration: underline;
}

.footer-link:hover i {
  color: var(--bs-primary);
}

.footer-btn-link {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
}

.footer-btn-link:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.footer-heading {
  font-size: 1.1rem;
}

.footer-logo {
  display: inline-block;
  text-decoration: none;
}

address {
  font-style: normal;
  line-height: 1.6;
}

.footer-cert-logo {
  display: block;
  height: 48px;
  width: auto;
  padding: 4px;
  border-radius: 4px;
  background-color: #fff;
}

.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

@media (max-width: 576px) {
  .footer-link {
    font-size: 0.9rem;
  }
}
</style>
